import { Truck, IndianRupee, Star, TrendingUp, MapPin, Clock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { CompactStatCard, PageHeader, StatsGrid } from "@/components/shared";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useDeliveryDashboard, useDeliveryOrders, useDeliveryPartnerId } from "@/hooks/queries";
import { computeCompletionRate } from "@/lib/mappers/delivery-mappers";

const statusColor: Record<string, string> = {
  assigned: "bg-info/10 text-info border-info/20",
  picked_up: "bg-warning/10 text-warning border-warning/20",
  in_transit: "bg-accent/10 text-accent border-accent/20",
  delivered: "bg-success/10 text-success border-success/20",
};

const DeliveryDashboard = () => {
  const { partnerId, isLoading: partnerLoading } = useDeliveryPartnerId();
  const { data: stats, isLoading: statsLoading } = useDeliveryDashboard(partnerId);
  const { data: ordersData, isLoading: ordersLoading } = useDeliveryOrders(partnerId);

  const isLoading = partnerLoading || statsLoading || ordersLoading;
  const orders = ordersData?.items ?? [];
  const activeOrders = orders.filter((o) => o.status !== "delivered");

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-24 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  if (!partnerId || !stats) {
    return (
      <PageHeader title="Dashboard" description="No delivery partner profile linked to your account." />
    );
  }

  const completionRate = computeCompletionRate(stats);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Dashboard"
        description="Your delivery overview for today."
      />

      <StatsGrid>
        <CompactStatCard title="Today's Deliveries" value={stats.todayDeliveries} icon={Truck} iconClassName="bg-gradient-accent" />
        <CompactStatCard title="Today's Earnings" value={`₹${stats.todayEarnings}`} icon={IndianRupee} />
        <CompactStatCard title="Total Earnings" value={`₹${stats.totalEarnings.toLocaleString()}`} icon={TrendingUp} />
        <CompactStatCard title="Rating" value={`⭐ ${stats.rating.toFixed(1)}`} icon={Star} />
      </StatsGrid>

      <Card>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Completion Rate</p>
              <p className="text-xl font-heading font-bold">{completionRate}%</p>
              <Progress value={completionRate} className="h-2 mt-2" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Deliveries</p>
              <p className="text-xl font-heading font-bold">{stats.totalDeliveries}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Today: {stats.todayDeliveries}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Earnings</p>
              <p className="text-xl font-heading font-bold">₹{stats.totalEarnings.toLocaleString()}</p>
              <p className="text-[10px] text-success mt-1">Pending: ₹{stats.pendingSettlement.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Active Orders</p>
              <p className="text-xl font-heading font-bold">{stats.pendingDeliveries}</p>
              <p className="text-[10px] text-muted-foreground mt-1">Assigned to you right now</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Active Deliveries</CardTitle>
            <Button variant="ghost" size="sm" className="text-xs" asChild>
              <Link to="/delivery/orders">View all <ArrowRight className="ml-1 h-3 w-3" /></Link>
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {activeOrders.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">No active deliveries. Go online to receive orders!</p>
          ) : activeOrders.slice(0, 5).map((order) => (
            <div key={order.id} className="rounded-lg border border-border p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-sm">{order.orderId}</p>
                  <p className="text-xs text-muted-foreground">{order.items} items • ₹{order.total}</p>
                </div>
                <Badge variant="outline" className={statusColor[order.status]}>{order.status.replace("_", " ")}</Badge>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Pickup: {order.storeName}</p>
                    <p className="text-muted-foreground">{order.storeAddress}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-3.5 w-3.5 text-destructive mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Drop: {order.customerName}</p>
                    <p className="text-muted-foreground">{order.customerAddress}</p>
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-between pt-2 border-t border-border">
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{order.distance}</span>
                  <span>•</span>
                  <span><Clock className="inline h-3 w-3 mr-0.5" />{order.estimatedTime}</span>
                  <span>•</span>
                  <span className="font-semibold text-success">₹{order.commission} commission</span>
                </div>
                {order.paymentMethod === "cod" && order.codAmount && (
                  <Badge variant="outline" className="bg-warning/10 text-warning border-warning/20 text-[10px]">
                    COD ₹{order.codAmount}
                  </Badge>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default DeliveryDashboard;
